import { useCallback, useEffect, useState } from 'react';

import { Button } from '@components/button';
import { IconShadowContainer } from '@components/icon-shadow-container';
import { IconBrandJourneysGradient } from '@components/icons/brand-journeys-gradient';
import { IconSwap } from '@components/icons/swap';
import { AuthInfoItems, BASE_URL_FIELD } from '@components/integrations/auth-info-items';
import type { AuthInfoBaseUrl } from '@components/integrations/auth-info-items';
import { getBaseUrlAffixes, getDefaultBaseUrl } from '@components/integrations/auth-info-layout';
import { IntegrationIcon } from '@components/integrations/common';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import {
  useConnectMarketplaceTemplate,
  useInvalidateQueriesAfterConnect,
  useReconnectIntegration,
} from 'api/app-marketplace/hooks';
import { initiateOAuthAuthorize, MarketplaceTemplate } from 'api/app-marketplace/requests';

export type ConnectModalProps = {
  template: MarketplaceTemplate;
  authentication?: MarketplaceTemplate['authentications'][number];
  isEdit?: boolean;
  journeyActionId?: string;
  onSuccess?: () => void;
};

export const useConnectModal = () => useModal(ConnectModal, { variant: 'REGULAR' });

export const ConnectModal = ({
  template,
  authentication,
  isEdit,
  journeyActionId,
  onSuccess,
}: ConnectModalProps) => {
  const { t } = useScopedTranslate(locale);
  const authentications = template.authentications;
  const selectedAuthentication = authentication ?? authentications[0];
  const info = selectedAuthentication?.info?.info ?? [];
  const isOAuthConnect = selectedAuthentication?.authType === 'oauth_connect';

  const initialValues: Record<string, string> = {};
  if (isEdit && template.connectionDetails) {
    const editableFields = new Set<string>();
    for (const item of info) {
      if (item.type === 'input' && 'field' in item && item.field) {
        editableFields.add(item.field);
      } else if (item.type === 'base-url') {
        editableFields.add(BASE_URL_FIELD);
      }
    }

    Object.entries(template.connectionDetails).forEach(([key, value]) => {
      if (key === 'connectionVariables' && typeof value === 'object' && value !== null) {
        Object.entries(value as Record<string, string>).forEach(([varKey, varValue]) => {
          if (typeof varValue === 'string') {
            initialValues[varKey] = varValue;
          }
        });
      } else if (editableFields.has(key) && typeof value === 'string') {
        initialValues[key] = value;
      }
    });
  }

  // Only a connection with no base URL of its own starts on the template's default,
  // so reconnecting keeps whichever option was saved.
  if (!initialValues[BASE_URL_FIELD]) {
    const defaultBaseUrl = getDefaultBaseUrl(info);
    if (defaultBaseUrl) {
      initialValues[BASE_URL_FIELD] = defaultBaseUrl;
    }
  }

  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const connectMutation = useConnectMarketplaceTemplate();
  const reconnectMutation = useReconnectIntegration(template.shortId);
  const invalidateQueriesAfterConnect = useInvalidateQueriesAfterConnect(template.shortId);

  const handleChange = (field: string, value: string) => {
    if (!field) return;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleOAuthResult = useCallback(
    (success: boolean) => {
      if (success) {
        invalidateQueriesAfterConnect();
        onSuccess?.();
        hideModal();
      }
    },
    [onSuccess, invalidateQueriesAfterConnect]
  );

  useEffect(() => {
    // Method 1: Listen for postMessage from popup
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'oauth_callback') {
        handleOAuthResult(event.data.success);
      }
    };

    // Method 2: Listen for localStorage changes (fallback for when window.opener is lost)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'oauth_callback_result' && event.newValue) {
        try {
          const result = JSON.parse(event.newValue);
          if (result.timestamp && Date.now() - result.timestamp < 10000) {
            handleOAuthResult(result.success);
            localStorage.removeItem('oauth_callback_result');
          }
        } catch {
          // Invalid JSON
        }
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleOAuthResult]);

  const prepareValues = () => {
    const baseUrlItem = info.find((item): item is AuthInfoBaseUrl => item.type === 'base-url');
    const { prefix, postfix } = getBaseUrlAffixes(baseUrlItem);

    const preparedValues = { ...values };
    if (preparedValues[BASE_URL_FIELD] !== undefined) {
      let baseUrl = preparedValues[BASE_URL_FIELD];
      if (prefix && !baseUrl.startsWith(prefix)) {
        baseUrl = `${prefix}${baseUrl}`;
      }
      if (postfix && !baseUrl.endsWith(postfix)) {
        baseUrl = `${baseUrl}${postfix}`;
      }
      preparedValues[BASE_URL_FIELD] = baseUrl;
    }
    return preparedValues;
  };

  const handleOAuthConnect = async () => {
    if (!selectedAuthentication) return;

    const preparedValues = prepareValues();

    const { authorizationUrl } = await initiateOAuthAuthorize({
      authenticationShortId: selectedAuthentication.shortId,
      journeyActionId,
      values: Object.keys(preparedValues).length > 0 ? preparedValues : undefined,
    });

    const width = 504;
    const height = 648;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    window.open(
      authorizationUrl,
      'oauth_popup',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );
  };

  const handleConnect = async () => {
    const preparedValues = prepareValues();

    if (isEdit && template.connectedIntegrationId) {
      await reconnectMutation.mutateAsync({
        integrationId: template.connectedIntegrationId,
        authenticationId: selectedAuthentication?.shortId,
        values: preparedValues,
        ...(journeyActionId && { journeyActionId }),
      });
    } else {
      if (!selectedAuthentication) return;
      await connectMutation.mutateAsync({
        templateId: template.shortId,
        authenticationId: selectedAuthentication.shortId,
        values: preparedValues,
        ...(journeyActionId && { journeyActionId }),
      });
    }
    invalidateQueriesAfterConnect();
    onSuccess?.();
    hideModal();
  };

  const isLoading = connectMutation.isLoading || reconnectMutation.isLoading;

  return (
    <Modal>
      <Modal.Header>
        <Stack orientation="horizontal" align="center" justify="space-between">
          <Modal.HeaderTitle title={t('connectTitle', { name: template.name })} />
          <IntegrationSwapIcons template={template} />
        </Stack>
      </Modal.Header>
      <Modal.Content>
        <AuthInfoItems items={info} values={values} onChange={handleChange} />
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end" space={2}>
          <Button variant="secondary" text={t('cancel')} onClick={hideModal} disabled={isLoading} />
          <Button
            variant="primary-blue"
            text={t('connect')}
            onClick={isOAuthConnect ? handleOAuthConnect : handleConnect}
            disabled={!selectedAuthentication || isLoading}
          />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const IntegrationSwapIcons = ({ template }: { template: MarketplaceTemplate }) => {
  return (
    <Stack orientation="horizontal" align="center" space={1}>
      <IconShadowContainer icon={<IconBrandJourneysGradient />} />
      <IconSwap size={24} color="uiTrans20" />
      <IconShadowContainer icon={<IntegrationIcon src={template.logo} />} />
    </Stack>
  );
};

const locale = {
  en: {
    connectTitle: 'Connect {name}',
    cancel: 'Cancel',
    connect: 'Connect',
  },
};
